import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { seguradoraService } from '@/services/api';
import { Seguradora } from '@/types';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

const seguradoraSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
});

type SeguradoraFormData = Omit<Seguradora, 'id'>;

export const NovaSeguradoraPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!id;
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateSeguradoras } = useQueryInvalidation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<SeguradoraFormData>({
    resolver: zodResolver(seguradoraSchema),
  });

  // Buscar dados da seguradora (se editando)
  const { data: seguradoraData, isLoading: loadingSeguradora } = useQuery({
    queryKey: ['seguradora', id],
    queryFn: () => seguradoraService.getById(Number(id)),
    enabled: isEditing,
  });

  const createSeguradoraMutation = useMutation({
    mutationFn: seguradoraService.create,
    onSuccess: () => {
      invalidateSeguradoras();
      handleSuccess('Seguradora criada com sucesso!');
      navigate('/seguradoras');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao criar seguradora');
    },
  });

  const updateSeguradoraMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: SeguradoraFormData }) =>
      seguradoraService.update(id, data),
    onSuccess: () => {
      invalidateSeguradoras();
      handleSuccess('Seguradora atualizada com sucesso!');
      navigate('/seguradoras');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao atualizar seguradora');
    },
  });

  // Preencher formulário com dados existentes (se editando)
  useEffect(() => {
    if (seguradoraData) {
      setValue('nome', seguradoraData.nome);
    }
  }, [seguradoraData, setValue]);

  const onSubmit = async (data: SeguradoraFormData) => {
    try {
      setIsSubmitting(true);
      if (isEditing) {
        await updateSeguradoraMutation.mutateAsync({ id: Number(id), data });
      } else {
        await createSeguradoraMutation.mutateAsync(data);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state para edição
  if (isEditing && loadingSeguradora) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando dados da seguradora...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Seguradora' : 'Nova Seguradora'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing 
            ? 'Edite as informações da seguradora' 
            : 'Cadastre uma nova seguradora no sistema'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Dados da Seguradora</CardTitle>
            <CardDescription>
              Informações básicas da seguradora
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="nome">Nome da Seguradora *</Label>
              <Input
                id="nome"
                placeholder="Ex: Porto Seguro, Bradesco Seguros"
                {...register('nome')}
                className={errors.nome ? 'border-error-500' : ''}
              />
              {errors.nome && (
                <p className="mt-1 text-sm text-error-600">{errors.nome.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/seguradoras')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || loadingSeguradora}
          >
            {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar Seguradora' : 'Criar Seguradora')}
          </Button>
        </div>
      </form>
    </div>
  );
};
